<template>
    <!-- Main Sidebar -->
    <a-layout-sider collapsible class="sider-primary" breakpoint="lg" collapsed-width="60" width="250px"
        :collapsed="sidebarCollapsed" @collapse="$emit('toggleSidebar', !sidebarCollapsed)" :trigger="null" :class="[
            'ant-layout-sider-' + sidebarColor,
            'ant-layout-sider-' + sidebarTheme,
        ]" theme="light" :style="{ backgroundColor: 'transparent' }">
        <div class="brand">
            <router-link to="/loads/loads-list" class="logo-link">
                <img v-if="!logoError" 
                     :src="logoUrl" 
                     alt="GÜRMAN LOGISTICS" 
                     class="logo-image" 
                     @error="handleLogoError" 
                     @load="handleLogoLoad" />
                <div class="logo-fallback" v-if="logoError">
                    <span class="logo-text-fallback">GL</span>
                </div>
                <div class="title-container">
                    <img src="/images/gurman-title.png" alt="GÜRMAN LOGISTICS" class="title-image" />
                </div>
            </router-link>
        </div>
        
        <!-- Sidebar Navigation Menu -->
        <a-menu theme="light" mode="inline" :open-keys="openKeys" @openChange="onOpenChange">
            <a-menu-item class="menu-item-header"> Content </a-menu-item>
            <a-menu-item>
                <router-link to="/loads/loads-list">
                    <span class="icon">
                        <a-icon type="car" theme="filled" class="m-0" />
                    </span>
                    <span class="label">Loads</span>
                </router-link>
            </a-menu-item>
            <!-- Show Companies and Users only for admin users -->
            <a-menu-item v-if="isAdminUser">
                <router-link to="/companies/companies-list">
                    <span class="icon">
                        <a-icon type="control" theme="filled" class="m-0" />
                    </span>
                    <span class="label">Companies</span>
                </router-link>
            </a-menu-item>
            <a-menu-item v-if="isAdminUser">
                <router-link to="/users/users-list">
                    <span class="icon">
                        <a-icon type="contacts" theme="filled" class="m-0" />
                    </span>
                    <span class="label">Users</span>
                </router-link>
            </a-menu-item>
            <!-- Show Admins only for superadmin users -->
            <a-menu-item v-if="isSuperAdminUser">
                <router-link to="/admins/admins-list">
                    <span class="icon">
                        <a-icon type="crown" theme="filled" class="m-0" />
                    </span>
                    <span class="label">Admins</span>
                </router-link>
            </a-menu-item>
        </a-menu>

        <!-- Logged-in user info card (pinned to bottom) -->
        <div class="user-card">
            <div class="user-card-avatar">{{ userInitials }}</div>
            <div class="user-card-info">
                <div class="user-card-name">{{ userName }}</div>
                <div class="user-card-email">{{ userEmail }}</div>
            </div>
        </div>
    </a-layout-sider>
    <!-- / Main Sidebar -->
</template>

<script>
import VueCookies from 'vue-cookies';
import VueJwtDecode from 'vue-jwt-decode'
export default {
    props: {
        // Sidebar collapsed status.
        sidebarCollapsed: {
            type: Boolean,
            default: false,
        },

        // Main sidebar color.
        sidebarColor: {
            type: String,
            default: "primary",
        },

        // Main sidebar theme : light, white, dark.
        sidebarTheme: {
            type: String,
            default: "light",
        },
    },
    data() {
        return {
            rootSubmenuKeys: [
                "pages",
                "applications",
                "ecommerce",
                "authentication",
                "basic",
                "components",
                "changelog",
            ],
            openKeys: this.$route.meta.sidebarMap,
            logoError: false,
        };
    },
    computed: {
        // Check if current user is admin
        isAdminUser() {
            return localStorage.getItem('userType') === 'admin';
        },
        // Check if current user is superadmin
        isSuperAdminUser() {
            if (localStorage.getItem('userType') !== 'admin') {
                return false;
            }
            // Check admin_type from stored user data
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                try {
                    const userData = JSON.parse(userDataString);
                    return userData.admin && userData.admin.admin_type === 'superadmin';
                } catch (e) {
                    console.error('Error parsing user data:', e);
                    return false;
                }
            }
            return false;
        },

        // Logged-in user (admin or user) stored at login
        currentUser() {
            try {
                const raw = localStorage.getItem('user');
                return raw ? JSON.parse(raw) : null;
            } catch (e) {
                return null;
            }
        },

        userName() {
            return (this.currentUser && this.currentUser.full_name) || 'User';
        },

        userEmail() {
            return (this.currentUser && this.currentUser.email) || '';
        },

        userInitials() {
            const name = this.userName;
            if (!name) return 'U';
            return name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        logoUrl() {
            // Add cache-busting parameter to prevent caching issues
            const timestamp = process.env.NODE_ENV === 'production'
                ? Date.now()
                : '1'; // Use fixed value in development for consistency
            // Use admin logo for admin users
            const logoFile = this.isAdminUser ? 'gurman-logo_admin.png' : 'gurman-logo.png';
            return `/images/${logoFile}?v=${timestamp}`;
        },
    },
    methods: {
        onOpenChange(openKeys) {
            const latestOpenKey = openKeys.find(
                (key) => this.openKeys.indexOf(key) === -1
            );

            if (this.rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
                this.openKeys = openKeys;
            } else {
                this.openKeys = latestOpenKey ? [latestOpenKey] : [];
            }
        },
        
        handleLogoError(event) {
            console.error('Logo failed to load:', event.target.src);
            console.error('Attempting fallback...');
            this.logoError = true;
            // Try alternative paths
            this.tryAlternativePaths();
        },

        handleLogoLoad(event) {
            console.log('Logo loaded successfully:', event.target.src);
            this.logoError = false;
        },

        tryAlternativePaths() {
            // List of alternative paths to try
            const alternatives = [
                './images/gurman-logo.png',
                '/static/images/gurman-logo.png',
                '/public/images/gurman-logo.png'
            ];
            
            // This is just for debugging - in production, the fallback will be shown
            alternatives.forEach(path => {
                console.log('Alternative path to try:', path);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
// Reserve room at the bottom so the menu never hides behind the fixed footer bar
:deep(.ant-layout-sider-children) {
	padding-bottom: 88px !important;
}

// Logged-in user info — full-width footer bar pinned to the very bottom.
// Positioned against the sider (which is position: fixed), so it does not
// depend on the inner height chain.
.user-card {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 2;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px 20px;
	background: #ffffff;
	border-top: 1px solid #e8e8e8;

	.user-card-avatar {
		width: 36px;
		height: 36px;
		border-radius: 50%;
		background: #1890ff;
		color: #ffffff;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 600;
		font-size: 13px;
		line-height: 1;
		text-align: center;
		flex-shrink: 0;
	}

	.user-card-info {
		overflow: hidden;
	}

	.user-card-name {
		font-weight: 600;
		font-size: 14px;
		color: #141414;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.user-card-email {
		font-size: 12px;
		color: #8c8c8c;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

// Collapsed (icon-only) sidebar: show just the avatar, centered
:deep(.ant-layout-sider-collapsed) .user-card,
:deep(.ant-layout-sider[style*="width: 60px"]) .user-card {
	padding: 12px 8px;
	justify-content: center;

	.user-card-info {
		display: none;
	}
}

// Minimized sidebar (expands on hover): hide text until hovered
.layout-dashboard.sidebar-minimized.has-sidebar .ant-layout-sider.sider-primary:not(:hover) .user-card {
	justify-content: center;

	.user-card-info {
		display: none;
	}
}

.brand {
	.logo-link {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 16px 20px;
	}
	
	.logo-image {
		width: 60px;
		height: 66px;
		object-fit: contain;
		transition: all 0.3s ease-in-out;
		flex-shrink: 0;
	}
	
	.title-container {
		display: flex;
		align-items: center;
		transition: all 0.3s ease-in-out;
	}
	
	.title-image {
        padding-top: 17px;
		height: 62px;
        width: 160px !important;
		max-width: 160px;
		object-fit: contain;
		transition: all 0.3s ease-in-out;
	}
}

.logo-link {
	display: block;
	text-decoration: none;
	color: inherit;
	
	&:hover {
		opacity: 0.8;
		transition: opacity 0.2s ease-in-out;
	}
}

// Icon styles for menu items
.icon {
	display: inline-flex;
	width: 32px;
	height: 32px;
	background-color: #ffffff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	border-radius: 6px;
	justify-content: center;
	align-items: center;
	margin-right: 11px;
	vertical-align: middle;
	transition: all 0.3s ease-in-out;
	
	.anticon {
		font-size: 16px;
		color: #6B7280;
	}
}

// When sidebar is minimized, ensure icons remain visible
:deep(.sidebar-minimized) {
	.icon {
		margin-right: 0;
		width: 32px;
		height: 32px;
		display: inline-flex;
		justify-content: center;
		align-items: center;
	}
	
	.label {
		display: none;
	}
}
.layout-dashboard.sidebar-minimized.has-sidebar .ant-layout-sider.sider-primary:not(:hover) .brand .title-image {
    display: none !important;
}

// When sidebar is minimized, adjust logo layout
:deep(.ant-layout-sider-collapsed) .brand .logo-link {
	padding: 16px 10px;
	justify-content: center;
}

:deep(.ant-layout-sider-collapsed) .brand .logo-image {
	width: 40px;
	height: 40px;
}

// Logo fallback styles
.logo-fallback {
	width: 60px;
	height: 60px;
	background: linear-gradient(135deg, #1890ff, #722ed1);
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease-in-out;
	flex-shrink: 0;
	
	.logo-text-fallback {
		color: white;
		font-weight: bold;
		font-size: 20px;
		letter-spacing: 2px;
	}
}

::deep(.ant-layout-sider-collapsed) .brand .logo-fallback {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	
	.logo-text-fallback {
		font-size: 14px;
		letter-spacing: 1px;
	}
}

// Collapsed sidebar logo styles
.collapsed-brand {
	display: none; // Hidden by default
	align-items: center;
	justify-content: center;
	height: 60px;
	padding: 5px;
	
	.collapsed-logo {
		max-width: 50px;
		max-height: 50px;
		width: auto;
		height: auto;
		object-fit: contain;
	}
	
	.collapsed-logo-fallback {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 50px;
		width: 50px;
		background-color: #f5f5f5;
		border-radius: 4px;
		
		.collapsed-logo-text {
			font-size: 18px;
			font-weight: bold;
			color: #1890ff;
			text-align: center;
			letter-spacing: 1px;
		}
	}
}

// Use CSS to show collapsed logo when sidebar is collapsed (based on Ant Design's collapsed class)
:deep(.ant-layout-sider-collapsed) .brand {
	display: none !important;
}

:deep(.ant-layout-sider-collapsed) .collapsed-brand {
	display: flex !important;
}

:deep(.ant-layout-sider-collapsed) .ant-menu {
	display: none !important;
}

:deep(.ant-layout-sider-collapsed) hr {
	display: none !important;
}

// Also handle the case when the sidebar has collapsed-width but not the collapsed class
:deep(.ant-layout-sider[style*="width: 60px"]) .brand {
	display: none !important;
}

:deep(.ant-layout-sider[style*="width: 60px"]) .collapsed-brand {
	display: flex !important;
}

:deep(.ant-layout-sider[style*="width: 60px"]) .ant-menu {
	display: none !important;
}

:deep(.ant-layout-sider[style*="width: 60px"]) hr {
	display: none !important;
}
</style>
